/**
 * @fileoverview Root Redux saga that combines all feature sagas.
 * 
 * This file imports and spawns all feature-level sagas, running them in parallel.
 * Using `spawn` instead of `fork` provides fault tolerance - if one saga fails,
 * others continue running.
 * 
 * @module rootSaga
 */

import { all, spawn } from 'redux-saga/effects';

import errorNotificationsSagas from './webcommon/errors/sagas';

import appSagas from './app/sagas';
import csaSummarySagas from './summary/csasummary/sagas';
import csaDrilldownSagas from './summary/csadrilldown/sagas';
import impactchartSagas from './summary/impactchart/sagas';
import monitoringSummary from './summary/monitoringSummary/sagas';
import errorsSagas from './common/errors/sagas';
import staticDataIsdaSagas from './staticdata/staticdataisda/sagas';
import commodityReportSagas from './commodityReport/sagas';
import fileAvailabilitySagas from './admin/fileAvailability/sagas';
import impactAnalysisSummarySagas from './impactanalysis/summary/sagas';
import pricingParamsSagas from './impactanalysis/pricingparameters/sagas';
import compareCsa from './impactanalysis/impactsummary/imimpactsummary/sagas';
import staticDataMappingsSagas from './staticdata/staticdatamappings/sagas';
import staticDataGeneral from './staticdata/staticdatageneral/sagas';
import downloadCrifSaga from './common/gridheader/downloadcrif/sagas';
import impactAnalysisSaga from './impactanalysis/sagas';
import webJobsSaga from './admin/webjobs/DataLoads/sagas';
import webJobsDetailsSaga from './admin/webjobs/DataDetails/sagas';
import dailyBatchHistSagas from './admin/dailyBatchHist/sagas';
import commodityJobsSaga from './commodityReport/triggerJobs/DataLoads/sagas';
import commodityJobsDetailsSaga from './commodityReport/triggerJobs/DataDetails/sagas';
import imCalculationSaga from './admin/imcalculation/sagas';
import userConfigurationSaga from './common/saveconfiguration/sagas';
import summaryIdGrid from './admin/impactAnalysisIdSummary/sagas';
import independentAmountSaga from './independentAmount/sagas';
import settingsSaga from './settings/sagas';
import auth from './auth/sagas';
import adminSagas from './admin/sagas';

/**
 * Root saga that spawns all feature sagas.
 * 
 * All sagas are spawned (not forked) to provide fault tolerance.
 * If one saga crashes, the others continue running independently.
 * 
 * Feature sagas included:
 * - Error notifications (UI notifications for errors)
 * - App initialization (CSA data, EOD date, SIMM version)
 * - Authentication (user permissions)
 * - Summary features (CSA summary, drilldown, impact chart, monitoring)
 * - Impact analysis (summary, pricing, comparisons)
 * - Static data (ISDA, mappings, general reference data)
 * - Admin (web jobs, IM calculations, file availability)
 * - Commodity reporting
 * - Independent amount calculations
 * - User settings and configuration
 * 
 * @generator
 * @yields {all} Array of spawned sagas running in parallel
 * 
 * @example
 * // In index.jsx
 * import rootSaga from './rootSaga';
 * const sagaMiddleware = createSagaMiddleware();
 * sagaMiddleware.run(rootSaga);
 */
export default function* rootSaga() {
    yield all([
        spawn(errorNotificationsSagas),
        spawn(appSagas),
        spawn(csaSummarySagas),
        spawn(csaDrilldownSagas),
        spawn(impactchartSagas),
        spawn(monitoringSummary),
        spawn(errorsSagas),
        spawn(staticDataIsdaSagas),
        spawn(commodityReportSagas),
        spawn(fileAvailabilitySagas),
        spawn(impactAnalysisSummarySagas),
        spawn(pricingParamsSagas),
        spawn(staticDataMappingsSagas),
        spawn(downloadCrifSaga),
        spawn(impactAnalysisSaga),
        spawn(webJobsSaga),
        spawn(webJobsDetailsSaga),
        spawn(dailyBatchHistSagas),
        spawn(commodityJobsSaga),
        spawn(commodityJobsDetailsSaga),
        spawn(imCalculationSaga),
        spawn(userConfigurationSaga),
        spawn(auth),
        spawn(compareCsa),
        spawn(staticDataGeneral),
        spawn(summaryIdGrid),
        spawn(independentAmountSaga),
        spawn(settingsSaga),
        spawn(adminSagas)
    ]);
}
